import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUpdateProject } from "@/hooks/use-projects";

interface ProjectEditDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  initialNombre: string;
  initialDescripcion: string;
}

export function ProjectEditDialog({
  open,
  onClose,
  projectId,
  initialNombre,
  initialDescripcion,
}: ProjectEditDialogProps) {
  const updateProject = useUpdateProject(projectId);
  const [nombre, setNombre] = useState(initialNombre);
  const [descripcion, setDescripcion] = useState(initialDescripcion);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!nombre.trim() || updateProject.isPending) return;
    updateProject.mutate(
      { nombre: nombre.trim(), descripcion: descripcion.trim() || undefined },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Editar proyecto"
      description="Cambia el nombre o la descripción del proyecto."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label
            htmlFor="project-edit-nombre"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            Nombre
          </label>
          <input
            id="project-edit-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
            maxLength={200}
            autoFocus
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
          />
        </div>
        <div className="space-y-1.5">
          <label
            htmlFor="project-edit-descripcion"
            className="block text-xs font-heading font-semibold text-ink uppercase tracking-wider"
          >
            Descripción (opcional)
          </label>
          <textarea
            id="project-edit-descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded border border-line bg-surface text-sm resize-none focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            placeholder="Breve descripción del proyecto…"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!nombre.trim() || updateProject.isPending}>
            {updateProject.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Guardar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
